import { Controller, FieldValues, Path, RegisterOptions, useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useRulesToDefaultMessage } from '@libs/forms';
import { DatePicker, DatePickerProps } from '@atoms/inputs';

type FormDatePickerProps<T extends FieldValues> = Omit<DatePickerProps, 'name'> & {
  name: Path<T>;
  required?: boolean;
  requiredMessage?: string;
  options?: RegisterOptions<T, Path<T>>;
  disableFuture?: boolean;
};
/**
 * Wrapper around `DatePicker` that integrates with react-hook-form.
 */
export function FormDatePicker<T extends FieldValues>({
  name,
  required,
  requiredMessage,
  options,
  defaultValue,
  disableFuture,
  ...props
}: FormDatePickerProps<T>) {
  const intl = useIntl();
  const { control } = useFormContext<T>();
  const parsedOptions = useRulesToDefaultMessage(options);
  const rules = {
    ...parsedOptions,
    ...(required
      ? {
          required: requiredMessage ?? intl.formatMessage({ id: 'forms.fieldIsRequired' }),
        }
      : {}),
  };

  return (
    <Controller<T>
      name={name}
      control={control}
      rules={rules}
      // @ts-expect-error - `DayJs` is used instead of `PathValue`
      defaultValue={defaultValue}
      render={({ field, fieldState: { error } }) => (
        <DatePicker
          {...props}
          {...field}
          defaultValue={defaultValue}
          required={required}
          error={!!error}
          helperText={error?.message}
          disableFuture={disableFuture}
        />
      )}
    />
  );
}
